import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Fingerprint } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { getSessionId, setSessionId } from '../utils/session';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PASSKEY_LS_KEY = 'le_passkey_id';

export const AuthPage = ({ onAuthenticated }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const [visitorCount, setVisitorCount] = useState(null);

  useEffect(() => {
    // Record and fetch visitor count on mount
    const recordVisit = async () => {
      try {
        const res = await axios.post(`${BACKEND_URL}/api/visitors`);
        setVisitorCount(res.data.count);
      } catch (e) { console.error('Visitor count error:', e); }
    };
    recordVisit();
  }, []);

  const handleBiometricAuth = async () => {
    setIsScanning(true);
    setError('');

    let biometricUsed = false;
    try {
      if (window.PublicKeyCredential) {
        const available = await PublicKeyCredential
          .isUserVerifyingPlatformAuthenticatorAvailable()
          .catch(() => false);

        if (available) {
          biometricUsed = true;

          // Safe localStorage access (throws in iOS private browsing)
          let storedId = null;
          try { storedId = localStorage.getItem(PASSKEY_LS_KEY); } catch (_) {}

          const challenge = () => crypto.getRandomValues(new Uint8Array(32));

          if (storedId) {
            // Existing passkey → authenticate (triggers Face ID / Touch ID)
            try {
              const credId = Uint8Array.from(atob(storedId), c => c.charCodeAt(0));
              await navigator.credentials.get({
                publicKey: {
                  challenge: challenge(),
                  rpId: window.location.hostname,
                  allowCredentials: [{ type: 'public-key', id: credId }],
                  userVerification: 'required',
                  timeout: 30000,
                },
              });
            } catch (_) {
              // Credential may have been removed from keychain — clear and allow in
              try { localStorage.removeItem(PASSKEY_LS_KEY); } catch (__) {}
            }
          } else {
            // First time (or storage cleared) → register a platform passkey
            try {
              const cred = await navigator.credentials.create({
                publicKey: {
                  challenge: challenge(),
                  rp: { name: 'Liquidity Engine Simulation', id: window.location.hostname },
                  user: {
                    id: crypto.getRandomValues(new Uint8Array(16)),
                    name: 'le-user',
                    displayName: 'Simulation User',
                  },
                  pubKeyCredParams: [
                    { type: 'public-key', alg: -7 },   // ES256
                    { type: 'public-key', alg: -257 },  // RS256
                  ],
                  authenticatorSelection: {
                    authenticatorAttachment: 'platform',
                    userVerification: 'required',
                    residentKey: 'preferred',
                  },
                  timeout: 30000,
                },
              });
              // Persist credential ID so future logins skip registration
              try {
                const b64 = btoa(String.fromCharCode(...new Uint8Array(cred.rawId)));
                localStorage.setItem(PASSKEY_LS_KEY, b64);
              } catch (_) {}
            } catch (createErr) {
              if (createErr.name === 'InvalidStateError') {
                // A passkey already exists on this device for this RP but our
                // localStorage was cleared — trigger discoverable-credential picker
                try {
                  await navigator.credentials.get({
                    publicKey: {
                      challenge: challenge(),
                      rpId: window.location.hostname,
                      allowCredentials: [],
                      userVerification: 'required',
                      timeout: 30000,
                    },
                  });
                } catch (_) {}
              }
              // NotAllowedError (user cancelled) or any other error → allow in
            }
          }
        }
      }
    } catch (_) { /* ignore capability checks */ }

    if (!biometricUsed) {
      await new Promise(resolve => setTimeout(resolve, 1500));
    }

    // Reuse existing session if one is already stored — avoids creating a new
    // session (and losing all simulation state) on every page refresh / re-auth.
    // Only create a fresh session when no session_id exists.
    const existingSessionId = getSessionId();

    if (!existingSessionId) {
      try {
        const res = await axios.post(`${BACKEND_URL}/api/auth/login`);
        const sessionId = res.data.session_id;
        setSessionId(sessionId);
      } catch (loginErr) {
        console.error('Session creation failed:', loginErr);
        // Fallback: generate a client-side UUID so the user can still proceed
        const fallbackId = ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
          (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16));
        setSessionId(fallbackId);
      }
    }

    setIsScanning(false);
    onAuthenticated();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/50 via-transparent to-transparent" />
      
      <div className="relative z-10 w-full max-w-sm px-4">
        {/* Title outside and above the box */}
        <div className="text-center mb-5">
          <h1 style={{ fontFamily: "'Manrope', sans-serif" }}
              className="text-lg font-extrabold tracking-wider text-indigo-700 uppercase whitespace-nowrap leading-none">
            LIQUIDITY ENGINE SIMULATION
          </h1>
          <p className="text-xs text-gray-500 tracking-wide font-medium mt-1">
            Usecase: Payment Processor
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass-panel rounded-xl p-10 space-y-7 shadow-xl"
        >

          <div className="flex flex-col items-center space-y-4 py-4">
            <motion.div
              animate={{
                scale: isScanning ? [1, 1.2, 1] : 1,
                opacity: isScanning ? [1, 0.5, 1] : 1,
              }}
              transition={{
                duration: 2,
                repeat: isScanning ? Infinity : 0,
                ease: "easeInOut"
              }}
              className="relative"
            >
              <div className={`absolute inset-0 rounded-full ${
                isScanning ? 'bg-indigo-200/40 blur-xl' : ''
              }`} />
              <div className={`relative bg-gradient-to-br from-indigo-600 to-indigo-700 p-6 rounded-full ${
                isScanning ? 'glow-primary' : ''
              }`}>
                <Fingerprint size={48} weight="duotone" className="text-white" />
              </div>
            </motion.div>

            {error && (
              <p className="text-red-600 text-sm" data-testid="auth-error">{error}</p>
            )}

            <Button
              onClick={handleBiometricAuth}
              disabled={isScanning}
              className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg btn-glow text-sm"
              data-testid="biometric-auth-button"
            >
              {isScanning ? (
                <span className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 bg-white rounded-full animate-pulse" />
                  Authenticating...
                </span>
              ) : (
                'Authenticate with Biometric'
              )}
            </Button>

            {visitorCount !== null && (
              <p className="text-xs text-gray-400 text-center font-mono" data-testid="visitor-count">
                {visitorCount.toLocaleString()} visits
              </p>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
